import {useNavigate} from "react-router-dom";
import {EditProductFormValues} from "../types";
import {message} from "antd";
import {useDigitalProductMutations} from "../../../../features/digital-products";
import {UpdateDigitalProductInput} from "../../../../features/digital-products/domain/digital-product.types";
import {toDigitalProductPricingInput} from "../../product-pricing";
import {toPurchaseLimitUpdatePayload} from "../../../../components/offering-form/general/settings/purchase-limit-payload";

interface HookResult {
  editProduct(values: EditProductFormValues): void;
  wasProductEdited: boolean;
  isEditingProduct: boolean;
  responseData?: any;
}

/**
 * Adapter: transforms form values to domain UpdateDigitalProductInput.
 */
function formValuesToUpdateInput(
  values: EditProductFormValues
): UpdateDigitalProductInput {
  const redirectType =
    values.redirectAfterPurchase === "custom-url"
      ? "url"
      : values.redirectAfterPurchase ?? "none";
  return {
    productId: values.id,
    name: values.name,
    description: values.description,
    ...toDigitalProductPricingInput(values),
    hideFromProfile: values.hideFromProfile,
    coverImage: values.coverImage,
    urlRedirect: redirectType === "url" ? values.url_redirect ?? undefined : undefined,
    redirectType,
    telegramGroupChatId:
      redirectType === "telegram" ? values.telegramGroupId ?? null : null,
    requestPhoneNumber: values.requestCustomerPhoneNumber,
    ...toPurchaseLimitUpdatePayload(values),
    bookingQuestions:
      values.bookingQuestions.length > 0
        ? values.bookingQuestions.map((q) => ({
            question: q.question,
            required: q.required,
            answerType: q.answerType,
            options:
              q.options && q.options.length > 0
                ? q.options.map((o) => o.optionLabel).filter(Boolean)
                : undefined,
          }))
        : [],
  };
}

export function useEditProductHook(): HookResult {
  const navigate = useNavigate();
  const {updateProduct, isUpdating, updateSuccess} = useDigitalProductMutations();

  const editProduct = (values: EditProductFormValues) => {
    const input = formValuesToUpdateInput(values);
    updateProduct(input, {
      onSuccess() {
        message.success("Updated Successfully");
        navigate("/products");
      },
      onError(error) {
        message.error(
          error.response?.data?.message || error.message || "Failed to update product"
        );
      },
    });
  };

  return {
    editProduct,
    wasProductEdited: updateSuccess,
    isEditingProduct: isUpdating,
    responseData: undefined,
  };
}
